import type { QueryClient } from "@tanstack/react-query";
import type { ChatThreadUpdatedEvent } from "@/features/notifications/hooks/useNotificationsWebSocket";
import type { ChatThread } from "@/types/chat";

/**
 * Applies a `chat.thread_updated` frame (BUG-723) to the caches the Chat page
 * and the sidebar read:
 *
 * - `["chat","unread-count"]` ← `unread_total` (the sidebar «Чат» badge), at
 *   once — it equals `GET /chat/unread-count/` by contract;
 * - the row's unread badge and time, at once, and the list re-sorted so a new
 *   message moves its thread up without waiting for the 30 s poll;
 * - then the list is invalidated: the frame carries no preview text (and a
 *   new thread is not in the list yet), so one refetch brings the row's
 *   preview from the server — never re-derived here;
 * - and that thread's side-panel context (BUG-739).
 */
export function applyThreadUpdate(
  queryClient: QueryClient,
  event: ChatThreadUpdatedEvent,
): void {
  queryClient.setQueryData<{ unread_count: number }>(["chat", "unread-count"], {
    unread_count: event.unread_total,
  });

  queryClient.setQueryData<ChatThread[]>(["chat", "threads"], (prev) => {
    if (!prev) return prev;
    const index = prev.findIndex((th) => th.id === event.thread_id);
    if (index === -1) return prev;
    const next = [...prev];
    next[index] = {
      ...next[index],
      unread_dispatcher_count: event.unread_dispatcher_count,
      last_message_at: event.last_message_at,
    };
    // The server's order: newest message first, empty threads last.
    return next.sort((a, b) => {
      if (a.last_message_at === b.last_message_at) return 0;
      if (a.last_message_at === null) return 1;
      if (b.last_message_at === null) return -1;
      return Date.parse(b.last_message_at) - Date.parse(a.last_message_at);
    });
  });

  void queryClient.invalidateQueries({ queryKey: ["chat", "threads"] });
  // BUG-739: the open thread's side panel (driver status, «Текущий рейс»)
  // is served from its own key and only polled every 30 s.
  void queryClient.invalidateQueries({
    queryKey: ["chat", "thread-context", event.thread_id],
  });
}
